import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import BuildInLoaderOverlay from '@rsos/components/capstone/Layout/AdminLayout/BuildInLoaderOverlay';
import { Text, Box, Button } from '@rsos/components/capstone/base';
import { selectUserOrgByApp } from '@rsos/sinatra';
import {
  captureExceptionWithScope,
  captureException,
} from '@rsos/utils/sentry';
import { notifySuccess, notifyError } from '@rsos/utils/toastUtils';
import {
  createIssue,
  deleteIssue,
  createIssueComment,
  updateIssue,
  fetchIssues,
} from '../../actions/issues';
import { getPSAPInfo } from '../../actions/psap';
import {
  MAX_FILE_SIZE,
  ACCEPTED_FILE_TYPES,
} from '../../constants/fileSizeType';
import {
  SHAPEFILE_VERIFIED,
  SHAPEFILE_SEEK_CLARIFICATION,
} from '../../constants/googleAnalyticsEvents';
import {
  SUBMITTED,
  PROCESSING,
  AWAITING_CUSTOMER_REVIEW,
  VERIFIED,
} from '../../constants/issueStatuses';
import {
  ViewModal,
  ReuploadModal,
  ReviewModal,
} from '../JurisdictionDetails/modals';
import EmptyState from './EmptyState/';
import {
  Description,
  Container,
  UploadInputContainer,
} from './JurisdictionDetails.styles';
import JurisdictionNoShapeFiles from './JurisdictionNoShapeFiles';
import ShapeFilesDetails from './ShapeFilesDetails';
import {
  trackJurisdictionDetailsAction,
  trackShapeFileVerified,
  trackSeekClarification,
} from './jurisdictionDetailsTrackings';

const JurisdictionDetails = () => {
  const fileRef = useRef(null);
  const dispatch = useDispatch();

  const sinatra = useSelector(state => state.sinatra);
  const application = sinatra.user.currentRole.application;
  const { irpVersion } = sinatra.irp;
  const org = selectUserOrgByApp(sinatra, application);
  const issues = useSelector(state => state.issues);
  const { fetching } = issues;
  const { currentPSAP } = useSelector(state => state.psaps);

  const [isReuploadModalVisible, setIsReuploadModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [reuploadedResult, setReuploadedResult] = useState(null);
  const [issueInReview, setIssueInReview] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState(null);

  const hasShapefiles = issues.records.length > 0;
  const uploadDisabled =
    !!issues.records.find(issue => issue.status === VERIFIED) ||
    issues.fetching ||
    issues.creating;

  const hasJurisdiction =
    currentPSAP.jurisdictions && currentPSAP.jurisdictions.length > 0;
  const scrollToMyRef = () => window.scrollTo(0, fileRef.current.offsetTop);
  useEffect(() => {
    dispatch(fetchIssues()).catch(action => {
      notifyError(`An error occurred while fetching issues: ${action.error}`);
      captureException(action.error);
    });
    if (window.location.href.includes('#boundary-upload')) {
      scrollToMyRef();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const doUpload = (shapefileContents, reupload = false) => {
    return dispatch(createIssue(shapefileContents))
      .then(() => {
        notifySuccess('Jurisdictional boundary uploaded');
        fileRef.current.value = null;
        const action = reupload ? 'reupload' : 'upload';
        trackJurisdictionDetailsAction(action, irpVersion);
        if (isReuploadModalVisible) {
          setIsReuploadModalVisible(prev => !prev);
        }
      })
      .catch(action => {
        const error = action.error || action;
        captureExceptionWithScope(new Error('UploadShapefileFailure'), {
          action,
          reupload,
        });
        notifyError(
          `Failed to upload jurisdictional boundary: ${error.message}`,
        );
        // Since the upload failed, the user will need to reselect their
        // files to trigger the upload again.
        fileRef.current.value = null;
        throw error;
      });
  };

  const handleCancelUpload = () => {
    fileRef.current.value = null;
    setIsReuploadModalVisible(false);
    setReuploadedResult(null);
  };

  const handleFileUpload = () => {
    const { files } = fileRef.current;
    const hasFile = files.length > 0;
    if (!hasFile) {
      return;
    }

    const file = files[0];
    const formData = new FormData();
    formData.append('shapefile', file);

    if (file.size > MAX_FILE_SIZE) {
      notifyError(
        'This file is too large and cannot be uploaded. Please reduce size and try again',
      );
      return handleCancelUpload();
    }
    const issueInReview = issues.records.find(
      issue =>
        issue.status === PROCESSING ||
        issue.status === AWAITING_CUSTOMER_REVIEW ||
        (issue.status === SUBMITTED && issue.shapefile_name !== null),
    );

    const emptyIssue = issues.records.find(
      issue => issue.status === SUBMITTED && issue.shapefile_name === null,
    );

    const hasUploadInReview = !!issueInReview;
    const hasEmptyUploadSubmitted = !!emptyIssue;

    if (hasUploadInReview) {
      setIsReuploadModalVisible(true);
      setIssueInReview(issueInReview);

      setReuploadedResult(formData);
    } else if (hasEmptyUploadSubmitted) {
      doUpload(formData).then(() => {
        dispatch(deleteIssue(emptyIssue)).catch(action => {
          captureExceptionWithScope(new Error('CloseShapefileIssueFailure'), {
            action,
            issue: issueInReview,
          });
        });
      });
    } else {
      doUpload(formData);
    }
  };

  const handleVerifyingIssue = issue => {
    dispatch(updateIssue(issue, VERIFIED))
      .then(() => {
        getPSAPInfo(org.name);
        setSelectedIssue(null);
        notifySuccess('Your jurisdictional boundary has been verified!');
        trackShapeFileVerified(SHAPEFILE_VERIFIED, irpVersion);
      })
      .catch(action => {
        const error = action.error || action;
        notifyError(
          `Failed to verify the jurisdictional boundary: ${error.message}`,
        );
      });
  };

  const handleCommentingIssue = (issue, comment, done) => {
    // When commenting, the issue should go back to the "Processing" status
    // after the comment posts successfully.
    dispatch(createIssueComment(issue, comment))
      .then(action => {
        notifySuccess('Your message has been posted!');
        dispatch(updateIssue(action.issue, PROCESSING)).then(action => {
          done();
          setSelectedIssue(action.issue);
          trackSeekClarification(SHAPEFILE_SEEK_CLARIFICATION, irpVersion);
        });
      })
      .catch(action => {
        const error = action.error || action;
        notifyError(`Failed to post your message: ${error.message}`);
      });
  };

  const handleReupload = () => {
    doUpload(reuploadedResult, true).then(() => {
      dispatch(deleteIssue(issueInReview)).catch(action => {
        captureExceptionWithScope(new Error('CloseShapefileIssueFailure'), {
          action,
          issue: issueInReview,
        });
      });
    });
  };
  const handleReview = issue => {
    setSelectedIssue(issue);
  };
  const handleView = () => {
    setSelectedIssue(null);
    setIsViewModalVisible(prev => !prev);
  };

  return (
    <>
      <Box horizontal justify="space-between" my={20} align="center">
        <Box flow={2}>
          <Description>
            Define and maintain your Jurisdictional details
          </Description>
          <Text size="small" inline color="secondaryText">
            We accept [zipped] folders composed of the following types:{' '}
            <Text inline fontWeight="bold">
              .shp, .shx, .dbf, .prj., .GeoJSON, .KML
            </Text>
          </Text>
        </Box>
        <Box position="relative">
          <Button btnType="primary" disabled={uploadDisabled} noMargin>
            Upload Files
          </Button>
          {!uploadDisabled && (
            <UploadInputContainer
              type="file"
              ref={fileRef}
              name="shapefile"
              accept={ACCEPTED_FILE_TYPES.join(', ')}
              onChange={handleFileUpload}
            ></UploadInputContainer>
          )}
        </Box>
      </Box>
      <>
        <Container fetching={fetching}>
          {fetching ? (
            <BuildInLoaderOverlay />
          ) : hasShapefiles ? (
            <ShapeFilesDetails
              handleReview={handleReview}
              handleView={handleView}
            />
          ) : hasJurisdiction ? (
            <JurisdictionNoShapeFiles handleView={handleView} />
          ) : (
            <EmptyState />
          )}
        </Container>
        <Text color="secondaryText" size="xsmall" m="5px" pb={15}>
          Have questions? Submit a ticket via the "Support" tab.
        </Text>
      </>
      {selectedIssue && (
        <ReviewModal
          isOpened
          issue={selectedIssue}
          onClose={() => {
            setSelectedIssue(null);
          }}
          onVerify={handleVerifyingIssue}
          onComment={handleCommentingIssue}
        />
      )}
      <ViewModal
        isOpened={isViewModalVisible}
        psap={currentPSAP}
        onClose={() => {
          setIsViewModalVisible(prev => !prev);
        }}
      />
      <ReuploadModal
        isOpened={isReuploadModalVisible}
        onConfirm={handleReupload}
        onClose={handleCancelUpload}
      />
    </>
  );
};

export default JurisdictionDetails;
